<template>
  <main class="admin-page">
    <div class="admin-shell">
      <MarketingToolHeader
        eyebrow="Leadership Academy"
        title="Leadership verwalten"
        description="Pflege Coaching-Inhalte, prüfe Praxis- und Video-Evaluationen und organisiere die 1:1-Sessions zentral."
        icon="mdi-shield-account-outline">
        <template v-slot:actions>
          <v-btn icon class="icon-action academy-local-back" aria-label="Zurück zu Leadership" title="Zurück zu Leadership" to="/akademie/leadership"
            ><v-icon>mdi-arrow-left</v-icon></v-btn
          >
          <span class="admin-badge"><v-icon>mdi-lock-outline</v-icon>Leadership Admin</span>
        </template>
      </MarketingToolHeader>

      <section class="contact-panel" aria-labelledby="leadership-contact-title">
        <div class="contact-panel-heading">
          <span class="contact-panel-icon"><v-icon>mdi-account-star-outline</v-icon></span>
          <div>
            <span>Verantwortung</span>
            <h2 id="leadership-contact-title">Leadership Kontaktperson</h2>
            <p>Diese Person erhält neue Leadership-Anfragen und Prüfungshinweise per E-Mail und Push.</p>
          </div>
        </div>

        <div class="contact-controls">
          <user-search
            v-model="contactSearchValue"
            label="Mitarbeiter suchen"
            :return-user="true"
            :rights="'aka_leadership_manage'"
            :disabled="loadingContact || savingContact || checkingContact"
            instructor
            dark
            outlined
            @update="selectContact"
            class="contact-search" />
          <v-btn
            color="#009640"
            depressed
            class="contact-save"
            :loading="savingContact"
            :disabled="!hasContactChange || loadingContact || checkingContact"
            @click="saveContact">
            <v-icon left>mdi-content-save-outline</v-icon>Kontaktperson speichern
          </v-btn>
        </div>

        <div v-if="checkingContact" class="contact-checking" role="status">
          <v-progress-circular indeterminate color="#7ee5a6" size="18" width="2" />
          Leadership-Adminrecht wird geprüft...
        </div>

        <div v-if="contactUser" class="contact-current">
          <span class="contact-avatar is-current">{{ userInitials(contactUser) }}</span>
          <div>
            <strong>{{ personName(contactUser) }}</strong>
            <span>{{ contactUser.email }}</span>
          </div>
          <span class="contact-channel" :class="{ 'is-missing': !contactUser.hasLeadershipManage }">
            <v-icon>{{ contactUser.hasLeadershipManage ? "mdi-shield-account-outline" : "mdi-alert-outline" }}</v-icon>
            {{ contactUser.hasLeadershipManage ? "Leadership Admin" : "Adminrecht fehlt" }}
          </span>
          <span v-if="contactUser.hasLeadershipManage" class="contact-channel"><v-icon>mdi-email-fast-outline</v-icon>E-Mail + Push</span>
        </div>
        <div v-else-if="!loadingContact" class="contact-empty"><v-icon>mdi-alert-circle-outline</v-icon>Noch keine Kontaktperson festgelegt.</div>

        <v-alert v-if="contactError" type="error" text outlined class="contact-alert">{{ contactError }}</v-alert>
        <p class="contact-note">
          <v-icon>mdi-information-outline</v-icon><span>Diese Auswahl verändert keine Rechte. Die Kontaktperson muss <code>aka_leadership_manage</code> bereits in der Rechteverwaltung besitzen.</span>
        </p>
      </section>

      <nav class="admin-nav" aria-label="Leadership Verwaltungsbereiche">
        <button
          v-for="(item, index) in navItems"
          :key="item.label"
          type="button"
          :class="{ active: adminTab === index }"
          :aria-current="adminTab === index ? 'page' : null"
          @click="selectTab(index)">
          <span
            ><v-icon>{{ item.icon }}</v-icon></span
          >
          <div>
            <strong>{{ item.label }}</strong
            ><small>{{ item.description }}</small>
          </div>
          <v-icon class="nav-arrow">mdi-chevron-right</v-icon>
        </button>
      </nav>

      <v-tabs-items v-model="adminTab" dark class="admin-content">
        <v-tab-item
          ><section class="admin-surface"><ModuleManager track="leadership" heading="Leadership-Module" /></section
        ></v-tab-item>
        <v-tab-item
          ><section class="admin-surface"><LeadershipReviewManager /></section
        ></v-tab-item>
        <v-tab-item
          ><section class="admin-surface"><DocumentManager area="leadership" /></section
        ></v-tab-item>
        <v-tab-item
          ><section class="admin-surface"><LeadershipCoachingAdmin /></section
        ></v-tab-item>
        <v-tab-item><section class="admin-surface"><LeadershipReports admin /></section></v-tab-item>
        <v-tab-item><section class="admin-surface"><AuditCallings /></section></v-tab-item>
      </v-tabs-items>
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right>{{ snackbar.text }}</v-snackbar>
    </div>
  </main>
</template>

<script>
import api from "@/api"
import LeadershipCoachingAdmin from "./LeadershipCoachingAdmin.vue"
import LeadershipReviewManager from "./LeadershipReviewManager.vue"
import DocumentManager from "./DocumentManager.vue"
import ModuleManager from "./ModuleManager.vue"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"
import userSearch from "@/components/userSearch"
import LeadershipReports from "../controlling/LeadershipReports.vue"
import AuditCallings from "../controlling/AuditCallings.vue"

export default {
  name: "LeadershipAdmin",
  components: { LeadershipReports, AuditCallings, LeadershipCoachingAdmin, LeadershipReviewManager, DocumentManager, ModuleManager, MarketingToolHeader, userSearch },
  data() {
    return {
      adminTab: 0,
      contactUser: null,
      selectedContact: null,
      contactSearchValue: null,
      contactCheckId: 0,
      loadingContact: false,
      checkingContact: false,
      savingContact: false,
      contactError: "",
      snackbar: { show: false, color: "success", text: "" },
      navItems: [
        { label: "Coaching-Inhalte", description: "Tools, Videos & Evaluationen", icon: "mdi-bookshelf" },
        { label: "Aufgaben-Prüfung", description: "Abgaben & Feedback", icon: "mdi-file-document-check-outline" },
        { label: "Leadership Docs", description: "Dokumente & Versionen", icon: "mdi-folder-key-outline" },
        { label: "Coaching 1:1", description: "Zeiten, Buchungen & Anfragen", icon: "mdi-account-clock-outline" },
        { label: "Leadership Reports", description: "Fristen, Abgaben & Jahresübersicht", icon: "mdi-file-chart-outline" },
        { label: "Audit Callings", description: "Center, Gespräche & Feedback", icon: "mdi-phone-check-outline" },
      ],
    }
  },
  computed: {
    hasContactChange() {
      const selectedId = this.selectedContact && this.selectedContact._id
      const currentId = this.contactUser && this.contactUser._id
      return Boolean(selectedId && selectedId !== currentId && this.selectedContact.hasLeadershipManage)
    },
  },
  mounted() {
    this.syncTab()
    this.loadContact()
  },
  watch: {
    "$route.query.tab"() {
      this.syncTab()
    },
  },
  methods: {
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    personName(user) {
      return [user && user.vorname, user && user.nachname].filter(Boolean).join(" ") || "Mitarbeiter"
    },
    userInitials(user) {
      return `${(user && user.vorname ? user.vorname.charAt(0) : "M")}${user && user.nachname ? user.nachname.charAt(0) : ""}`.toUpperCase()
    },
    validateContactSelection(user) {
      if (!user) {
        this.contactError = ""
        return
      }
      this.contactError = user.hasLeadershipManage
        ? ""
        : "Diese Person hat noch kein Leadership-Adminrecht. Bitte vergib zuerst aka_leadership_manage in der Rechteverwaltung."
    },
    async selectContact(user) {
      const checkId = ++this.contactCheckId
      this.contactSearchValue = user || null
      this.selectedContact = null
      this.contactError = ""
      if (!user || !user._id) {
        this.checkingContact = false
        return
      }

      this.checkingContact = true
      try {
        const result = await api.akademie.validateLeadershipContact(user._id)
        if (checkId !== this.contactCheckId) return
        const contactUser = (result && result.contactUser) || null
        this.selectedContact = contactUser
        this.contactSearchValue = contactUser || user
        this.validateContactSelection(contactUser)
      } catch (error) {
        if (checkId !== this.contactCheckId) return
        const data = error.response && error.response.data
        this.contactError = (data && (data.error || data.message)) || "Das Leadership-Adminrecht konnte nicht geprüft werden."
      } finally {
        if (checkId === this.contactCheckId) this.checkingContact = false
      }
    },
    async loadContact() {
      this.loadingContact = true
      this.contactError = ""
      try {
        const contactResult = await api.akademie.fetchLeadershipContact()
        const contactUser = (contactResult && contactResult.contactUser) || null
        this.contactUser = contactUser
        this.selectedContact = contactUser
        this.validateContactSelection(contactUser)
      } catch (error) {
        const data = error.response && error.response.data
        this.contactError = (data && (data.error || data.message)) || "Kontaktperson und Mitarbeitende konnten nicht geladen werden."
      } finally {
        this.loadingContact = false
      }
    },
    async saveContact() {
      if (!this.hasContactChange || this.savingContact) return
      this.savingContact = true
      this.contactError = ""
      try {
        const result = await api.akademie.updateLeadershipContact(this.selectedContact._id)
        const contactUser = (result && result.contactUser) || this.selectedContact
        this.contactUser = contactUser
        this.selectedContact = contactUser
        this.contactSearchValue = null
        this.notify("success", "Leadership Kontaktperson wurde gespeichert.")
      } catch (error) {
        const data = error.response && error.response.data
        this.contactError = (data && (data.error || data.message)) || "Die Leadership Kontaktperson konnte nicht gespeichert werden."
      } finally {
        this.savingContact = false
      }
    },
    syncTab() {
      const names = ["content", "reviews", "documents", "coaching", "reports", "audits"]
      const index = names.indexOf(this.$route.query.tab)
      if (index >= 0) this.adminTab = index
    },
    selectTab(index) {
      this.adminTab = index
      const names = ["content", "reviews", "documents", "coaching", "reports", "audits"]
      const query = { ...this.$route.query, tab: names[index] }
      if (names[index] !== "coaching") delete query.section
      if (names[index] !== "reports") delete query.report
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.admin-page {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(139, 92, 246, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.admin-shell {
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
}

.contact-panel {
  display: grid;
  gap: 0.875rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(88, 211, 136, 0.22);
  border-radius: 1.125rem;
  background: radial-gradient(circle at 100% 0%, rgba(0, 150, 64, 0.11), transparent 24rem), rgba(255, 255, 255, 0.03);
}

.contact-panel-heading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.75rem;
}

.contact-panel-icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.contact-panel-icon .v-icon {
  color: currentColor;
}

.contact-panel-heading > div {
  min-width: 0;
}

.contact-panel-heading > div > span {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.contact-panel-heading h2 {
  margin: 0.125rem 0 0;
  color: #fff;
  font-size: 1.125rem;
  line-height: 1.3;
}

.contact-panel-heading p {
  margin: 0.1875rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.contact-controls {
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) auto;
  align-items: start;
  gap: 0.625rem;
}

.contact-search {
  min-width: 0;
}

.contact-panel ::v-deep .v-input__slot {
  border-radius: 0.75rem !important;
  background: rgba(0, 0, 0, 0.14) !important;
}

.contact-panel ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.14);
}

.contact-save {
  min-height: 2.5rem;
  border-radius: 0.75rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.contact-checking {
  display: inline-flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.5rem;
  color: #7ee5a6;
  font-size: 0.875rem;
}

.contact-avatar {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 2.25rem;
  place-items: center;
  border: 1px solid rgba(88, 211, 136, 0.28);
  border-radius: 0.6875rem;
  color: #b8f0cc;
  background: rgba(0, 150, 64, 0.14);
  font-size: 0.75rem;
  font-weight: 800;
}

.contact-current {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.13);
  flex-wrap: wrap;
}

.contact-current > div {
  display: grid;
  min-width: 10rem;
  margin-right: auto;
}

.contact-current strong {
  color: #fff;
  font-size: 0.9375rem;
}

.contact-current div > span {
  color: #a3a3a8;
  font-size: 0.875rem;
}

.contact-channel {
  display: inline-flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.12);
  font-size: 0.75rem;
  font-weight: 750;
  white-space: nowrap;
}

.contact-channel .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.contact-channel.is-missing {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.11);
}

.contact-empty {
  display: flex;
  min-height: 3rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px dashed rgba(245, 196, 81, 0.25);
  border-radius: 0.875rem;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.06);
  font-size: 0.875rem;
}

.contact-empty .v-icon {
  color: currentColor;
}

.contact-alert {
  margin: 0 !important;
  font-size: 0.875rem;
  line-height: 1.5;
}

.contact-note {
  display: flex;
  align-items: flex-start;
  gap: 0.4375rem;
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.contact-note .v-icon {
  margin-top: 0.0625rem;
  color: #93c5fd;
  font-size: 1rem;
}

.contact-note code {
  color: #bfdbfe;
  font-size: 0.75rem;
  overflow-wrap: anywhere;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #fff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.admin-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.8125rem;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
  font-size: 0.875rem;
  font-weight: 750;
}

.admin-badge .v-icon {
  color: currentColor;
}

.admin-nav {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.625rem;
  margin: 1rem 0;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.026);
}

.admin-nav button {
  display: grid;
  min-height: 4.5rem;
  grid-template-columns: 2.625rem minmax(0, 1fr) 1.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.9375rem;
  outline: none;
  color: #d4d4d8;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.admin-nav button.active {
  border-color: rgba(139, 92, 246, 0.32);
  color: #fff;
  background: linear-gradient(120deg, rgba(139, 92, 246, 0.14), rgba(255, 255, 255, 0.035));
}

.admin-nav button:focus-visible {
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
}

.admin-nav button > span {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.055);
}

.admin-nav button.active > span {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.admin-nav button > span .v-icon {
  color: currentColor;
}

.admin-nav button > div {
  display: grid;
  min-width: 0;
}

.admin-nav strong {
  font-size: 0.875rem;
  line-height: 1.35;
}

.admin-nav small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.nav-arrow {
  color: #8f9095 !important;
}

.admin-content {
  overflow: visible;
  background: transparent !important;
}

.admin-surface {
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.03);
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.admin-surface ::v-deep .gold-text {
  color: #7ee5a6 !important;
}

@media (max-width: 59.9375rem) {
  .contact-controls {
    grid-template-columns: minmax(0, 1fr);
  }

  .contact-save {
    width: 100%;
  }

  .admin-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .admin-nav button {
    min-width: 0;
  }
}

@media (max-width: 47.99rem) {
  .admin-page {
    padding: 1rem;
  }

  .contact-panel {
    padding: 0.875rem;
    border-radius: 1rem;
  }

  .contact-current {
    align-items: flex-start;
  }

  .contact-current > div {
    width: calc(100% - 3rem);
    margin-right: 0;
  }

  .admin-surface {
    padding: 0.875rem;
    border-radius: 1.0625rem;
  }
}

@media (max-width: 37.4375rem) {
  .admin-page {
    padding: 0.75rem;
  }

  .admin-nav {
    margin: 0.75rem 0;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 1.0625rem;
  }

  .admin-nav button {
    min-height: 5.25rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 2.25rem auto;
    align-content: center;
    align-items: center;
    justify-items: center;
    gap: 0.375rem;
    padding: 0.625rem;
    border-color: rgba(255, 255, 255, 0.055);
    background: rgba(255, 255, 255, 0.025);
    text-align: center;
  }

  .admin-nav button > span {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.6875rem;
  }

  .admin-nav button > div {
    width: 100%;
    justify-items: center;
  }

  .admin-nav strong {
    width: 100%;
    line-height: 1.25;
    overflow-wrap: anywhere;
    text-align: center;
  }

  .admin-nav small,
  .admin-nav .nav-arrow {
    display: none;
  }

  .admin-surface {
    padding: 0.75rem;
  }
}
</style>
